import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, MapPin, Star, ArrowUpRight, Sparkles, ShieldCheck, Wallet } from "lucide-react";
import { LOCATIONS, MESSENGER_URL } from "@/data/staycation";
import Seo from "@/components/Seo";

function Hero() {
  return (
    <section className="relative" data-testid="home-hero">
      {/* Image banner — fills the full screen */}
      <div className="bg-neutral-950">
        <img
          src="https://customer-assets.emergentagent.com/job_affordable-staycay/artifacts/fv9ylmo3_FB%20PAGE.jpg"
          alt="Xanders Staycations — Time for your next staycation"
          className="hero-banner-img block w-full h-screen object-cover"
          loading="eager"
        />
      </div>

      {/* Tagline section — sits below the image so nothing overlaps */}
      <div className="bg-[var(--xs-bg)] border-t border-neutral-200">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 py-14 sm:py-20 text-center xs-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full bg-[var(--xs-primary-soft)] text-[var(--xs-primary)] px-3 py-1 text-xs font-semibold tracking-wide">
            <Sparkles className="h-3.5 w-3.5" /> 30 cozy units • Hosted by Alexander
          </span>
          <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.02] text-neutral-900">
            Feel at <span className="text-[var(--xs-primary)]">Home</span>.
          </h1>
          <p className="mt-5 mx-auto max-w-2xl text-base sm:text-lg text-neutral-600 leading-relaxed">
            Affordable staycations near SM Mall of Asia and SM Southmall. Bayside sunsets, resort-style pools, and warm Filipino hospitality — all wrapped in cozy, well-loved units.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={MESSENGER_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-messenger-cta"
              className="xs-btn-primary inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold"
            >
              <MessageCircle className="h-4 w-4" /> Book via Messenger
            </a>
            <Link
              to="/units"
              data-testid="hero-view-units"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-neutral-900 text-white hover:bg-neutral-800 px-6 py-3.5 text-sm font-semibold transition-colors"
            >
              View Available Units <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function LocationsStrip() {
  return (
    <section className="mx-auto max-w-7xl px-5 lg:px-8 py-20" data-testid="locations-section">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <p className="xs-eyebrow">Where we host</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2 max-w-xl">Four iconic addresses, one warm welcome.</h2>
        </div>
        <Link to="/units" className="text-sm font-semibold text-[var(--xs-primary)] hover:underline" data-testid="locations-view-all">
          See all units →
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {LOCATIONS.map((loc, idx) => (
          <Link
            key={loc.key}
            to={`/units?location=${loc.key}`}
            data-testid={`location-card-${loc.key}`}
            className="xs-card group block"
            style={{ animationDelay: `${idx * 80}ms` }}
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={loc.image} alt={loc.name} className="xs-img absolute inset-0 h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
              <div className="absolute bottom-0 p-5 text-white">
                <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold">
                  <MapPin className="h-3 w-3" /> {loc.area}
                </span>
                <h3 className="mt-1 font-display text-xl font-bold">{loc.name}</h3>
                <p className="text-sm text-white/85">{loc.near}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function WhyUs() {
  const items = [
    { Icon: Wallet, title: "Affordable nightly rates", text: "Starting from ₱1,750/night. No hidden fees, no security deposits." },
    { Icon: ShieldCheck, title: "Hosted by Alexander", text: "A Superhost on Airbnb who replies fast and treats guests like family." },
    { Icon: Sparkles, title: "Clean & cozy interiors", text: "Spotless units with all the essentials, ready for your weekend escape." },
  ];
  return (
    <section className="bg-[var(--xs-bg-alt)] border-y border-neutral-200" data-testid="why-us-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-3 gap-10">
        {items.map((it, idx) => (
          <div key={idx} className="flex gap-4">
            <div className="shrink-0 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white border border-neutral-200">
              <it.Icon className="h-5 w-5 text-[var(--xs-primary)]" />
            </div>
            <div>
              <h3 className="font-display text-xl font-bold">{it.title}</h3>
              <p className="text-sm text-neutral-600 mt-1 leading-relaxed">{it.text}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Testimonial() {
  return (
    <section className="mx-auto max-w-7xl px-5 lg:px-8 py-20" data-testid="home-testimonial">
      <div className="relative overflow-hidden rounded-3xl bg-white border border-neutral-200 p-8 sm:p-14">
        <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full bg-[var(--xs-primary-soft)] blur-3xl" aria-hidden />
        <p className="xs-eyebrow">Guest Stories</p>
        <p className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold max-w-3xl leading-snug">
          “Sobrang ganda ng unit at napakabait ni Kuya Alexander. Super smooth ang check-in via Messenger. Pool view talaga 10/10. Babalik kami!”
        </p>
        <div className="mt-6 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-[var(--xs-primary)] text-white inline-flex items-center justify-center font-bold">M</div>
          <div>
            <p className="text-sm font-semibold">Mariel Santos · Quezon City</p>
            <div className="flex items-center gap-0.5 text-[var(--xs-primary)]">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-current" />
              ))}
            </div>
          </div>
        </div>
        <Link to="/reviews" className="inline-flex items-center gap-1 mt-6 text-sm font-semibold text-[var(--xs-primary)] hover:underline" data-testid="home-view-reviews">
          Read all guest reviews <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-24" data-testid="home-cta">
      <div className="relative overflow-hidden rounded-3xl bg-[var(--xs-primary)] text-white p-10 sm:p-16">
        <div className="absolute -bottom-20 -left-10 w-80 h-80 rounded-full bg-white/10 blur-3xl" aria-hidden />
        <div className="relative max-w-2xl">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-white/80">Ready to staycation?</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-black leading-[1.05]">Your cozy bayside escape is one message away.</h2>
          <p className="mt-4 text-white/90 max-w-lg">Tell us your preferred dates and unit — we'll confirm availability via Messenger and walk you through the easy booking process.</p>
          <a
            href={MESSENGER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-[var(--xs-primary)] hover:bg-white/90 px-6 py-3.5 text-sm font-semibold transition-colors"
            data-testid="home-cta-messenger"
          >
            <MessageCircle className="h-4 w-4" /> Book via Messenger
          </a>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Cozy stays from ₱1,750/night near SM Mall of Asia"
        description="Book one of 30 affordable, hand-cared-for staycation units at Shell, Shore 1, Shore 2 & South Residences. Hosted by Airbnb Superhost Alexander — message us for instant booking."
        path="/"
      />
      <Hero />
      <LocationsStrip />
      <WhyUs />
      <Testimonial />
      <CTA />
    </div>
  );
}
